import { useDispatch } from 'react-redux'
import { RootState, TypedDispatch } from 'redux/store'
import { useGoogleReCaptcha } from 'react-google-recaptcha-ultimate'

import useOrder from 'modules/order/state/order/use-order'
import processFinalizeOrder from './process-finalize-order'
import useConfig from 'modules/config/state/use-config'
import useURLParams from 'modules/url-params/state/use-url-params'
import { getReCaptchaTokenAtExecutionTime } from 'utils/recaptcha'
import useReCaptchaEnabled from 'modules/app/hooks/recaptcha/useReCaptchaEnabled'
import { trackFormErrorEvent } from '../../analytics/helpers/track-form-events'

import useTrackPurchaseEvent from 'modules/analytics/hooks/useTrackPurchaseEvent'

/**
 * This method handles the finalization of the payment/order and
 * will redirect the user to the thank you page when succesful.
 */
const useFinalizeOrder: () => () => Promise<{ transactionId: number }> = () => {
  const order = useOrder()
  const dispatch = useDispatch<TypedDispatch<RootState>>()

  const { c_src, c_src2, referrer, utm } = useURLParams()
  const config = useConfig()

  // Get the hooks we need for recaptcha token generation at execution time
  const { executeV3 } = useGoogleReCaptcha()
  const isReCaptchaEnabled = useReCaptchaEnabled()

  const trackEvent = useTrackPurchaseEvent()

  const finalizeOrder = async () => {
    let reCaptchaToken: string | undefined

    if (!order.currency.value || !order.dcf.value.totalAmount) {
      throw new Error('The currency, and amount for the transaction must be defined.')
    }

    try {
      reCaptchaToken = await getReCaptchaTokenAtExecutionTime(executeV3, isReCaptchaEnabled)
    } catch (error) {
      // This is a technical error (ReCaptcha integration bug), not a user error
      trackFormErrorEvent('finalize-order', 'ReCaptcha integration error')
      throw new Error('Unable to process payment due to a technical issue. Please try again.')
    }

    // Create campaign object from config data
    const campaign = {
      campaign_id: parseInt(config.campaignId),
      campaign_data: {
        name: config.campaignName,
        currency_code: config.campaignCurrencyCode,
        designation_id: config.campaignDesignationId,
        goal: config.campaignGoal,
        created_at: config.campaignCreatedAt,
      },
    }

    const { error, transactionId } = await processFinalizeOrder(
      {
        organization: config.organization,
        campaign: campaign,
        c_src: c_src || undefined,
        c_src2: c_src2 || undefined,
        referrer: referrer ? referrer : document.referrer,
        currency: order.currency.value,
        baseAmount: order.dcf.value.totalAmount,
        frequency: order.frequency.value,
        recurringEndDate: order.recurringEndDate.value,
        utmParams: utm,
        reCaptchaToken,
      },
      dispatch,
    )

    if (error) {
      trackFormErrorEvent('finalize-order', error)
      throw error
    }

    if (transactionId) {
      trackEvent(transactionId)
      return { transactionId }
    } else {
      const err = 'Failed to finalize order. But did not receive error message.'
      trackFormErrorEvent('finalize-order', err)
      throw err
    }
  }

  return finalizeOrder
}

export default useFinalizeOrder
